import { useEffect } from 'react';
import { gql, useLazyQuery } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import { Logger } from 'shared-components/utils';

const logger = new Logger('AppVersion');

export const GET_APP_VERSION = gql`
  query GetAppVersion {
    getAppVersion {
      appVersion
    }
  }
`;

const POLL_INTERVAL_MS = 1000 * 60 * 60 * 2;

// The version this bundle was built against, stamped in at deploy time. It
// comes from the same source as the backend's RELEASE_VERSION (the git commit
// SHA via SENTRY_RELEASE), so the running tab can compare the server's current
// version directly against the build it loaded — no localStorage bookkeeping,
// and no spurious reload on the first load after an away-deploy (a fresh bundle
// already matches the server it was served by).
const getBundleVersion = (): string | undefined => import.meta.env.SENTRY_RELEASE;

const reload = (): void => {
  window.location.reload();
};

interface AppVersionProps {
  // getAppVersion now requires login on the backend. Skip the query for
  // unauthenticated sessions to avoid noisy 403s. Defaults to true to preserve
  // behaviour for consumers that haven't wired an auth signal yet.
  isLoggedIn?: boolean;
}

const AppVersion = ({ isLoggedIn = true }: AppVersionProps): JSX.Element | null => {
  const history = useHistory();

  const [getAppVersion, { data }] = useLazyQuery(GET_APP_VERSION, {
    // network-only so each call (mount, poll, nav) actually hits the server.
    // cache-and-network would short-circuit subsequent calls when the cache
    // is fresh, defeating the point of re-checking on navigation.
    fetchPolicy: 'network-only',
    pollInterval: POLL_INTERVAL_MS,
    errorPolicy: 'all',
    context: {
      retry: {
        max: 3,
        delay: 1000,
      },
    },
    onError: (error) => {
      // GraphQL errors mean the server rejected or mishandled the query, and
      // a networkError carrying a statusCode (ServerError/ServerParseError)
      // means the server responded with an HTTP failure — both actionable, so
      // report to Sentry. Pure transport failures (no response at all: device
      // sleep, page unload mid-request, flaky Wi-Fi) are expected for a
      // background freshness check and self-heal on the next poll or
      // navigation, so they stay out of Sentry (AU-HORIZON-PROD-6E5).
      const serverResponded =
        Boolean(error.graphQLErrors?.length) || (error.networkError != null && 'statusCode' in error.networkError);
      if (serverResponded) {
        logger.error('Error fetching app version', error);
        return;
      }
      logger.debug('Transient network error fetching app version', error);
    },
  });

  useEffect(() => {
    if (isLoggedIn) {
      getAppVersion();
    }
  }, [isLoggedIn, getAppVersion]);

  useEffect(() => {
    const serverVersion = data?.getAppVersion?.appVersion;
    const bundleVersion = getBundleVersion();
    // Only act once both versions are known. A bundle built without a stamped
    // version (e.g. local dev) has no safe baseline to compare against, so skip
    // rather than risk reloading on a placeholder.
    if (!serverVersion || !bundleVersion) return;

    if (serverVersion !== bundleVersion) {
      reload();
    }
  }, [data]);

  useEffect(() => {
    // Re-check the version on every route change so a freshly-deployed
    // bundle is picked up the moment the user navigates (e.g. clicking
    // Continue), instead of waiting up to POLL_INTERVAL_MS for the next poll.
    const unlisten = history.listen(() => {
      if (isLoggedIn) getAppVersion();
    });
    return unlisten;
  }, [history, isLoggedIn, getAppVersion]);

  return null;
};

export default AppVersion;
